import {
  displayGenericErrorToaster,
  displayGenericSuccessToaster,
  displayGenericWarningToaster
} from '@/api/helpers/helpers';
import { t } from '@/config/i18n';
/* TYPES */
import type { BatchResponse } from '@/types/batches/batches.types';

import { partition } from './generic';

/**
 * Function that allows to display the correct toaster according to batch action response
 * @param {BatchResponse} response - Response from the batch action
 * @param {string} successToaster - Success message to display
 * @param {number} nb_actions - Number of actions in the batch - allows to handle properly the cases there is no original response content
 * @param {boolean} showToaster - Boolean to display or not the toaster
 * @param {Function} callback - Callback
 * @param {any} queryClient - Query client for query invalidations
 */

export function handleBatchResponse({
  response,
  successToaster,
  nb_actions,
  showToaster = true,
  callback,
  queryClient
}: {
  response: BatchResponse;
  successToaster?: string;
  nb_actions: number;
  showToaster?: boolean;
  callback?: () => void;
  queryClient: any;
}) {
  let toasterType: 'success' | 'error' | 'warning' = 'success';
  let toasterMessage: string | undefined = successToaster;

  let errors: any[] = [];
  let successes: any[] = [];

  const isQueued = response && 'batch_uuid' in response;

  // Set toaster type and message according to response

  // IF QUEUED ACTION
  if (isQueued) {
    queryClient.invalidateQueries({ queryKey: ['getBatches'] });
    queryClient.invalidateQueries({ queryKey: ['getBatchesSummary'] });
    toasterMessage = t('recruiter_network.queue.toaster.success', { success: nb_actions });
  } else if (response) {
    // IF SYNC ACTION
    if (!Array.isArray(response)) {
      response = [response];
    }

    [errors, successes] = partition(response as any[], responseData => {
      return responseData?.status && !responseData.status.toString().startsWith('2');
    });

    if ((errors?.length && successes?.length) || (errors.length && response.length && response.length !== nb_actions)) {
      toasterType = 'warning';
      toasterMessage = t('recruiter_network.queue.modal.report.subtitle', { fail: errors.length });
    } else if (errors?.length) {
      toasterType = 'error';
      toasterMessage = t('general.error.general');
    }
  }
  // Display toaster according to type
  if (showToaster) {
    switch (toasterType) {
      case 'success':
        displayGenericSuccessToaster(toasterMessage);
        break;
      case 'error':
        displayGenericErrorToaster(toasterMessage);
        break;
      case 'warning':
        displayGenericWarningToaster(toasterMessage);
        break;
    }
  }

  // execute callback if action was not queued
  if (!isQueued && callback && toasterType !== 'error') {
    callback();
  }

  return {
    isQueued,
    errors,
    successes
  };
}

// SPECIFIC MANAGEMENT OF SEVERAL BATCHES SIMULTANEOUSLY - used for global add to group and add to campaign
export function handleMultipleBatches({
  context,
  response,
  queryClient,
  callback,
  totalCount,
  finalActionCount
}: {
  response: any;
  context: any;
  queryClient: any;
  callback?: () => void;
  totalCount: number;
  finalActionCount?: number;
}) {
  const globalResponse = response as any[];
  const batchedResources = globalResponse.filter(res => 'batch_uuid' in res);
  const batchedActions = batchedResources.length;

  const hasError = globalResponse.filter(res => res?.status && !res.status.toString().startsWith('2')).length;

  if (batchedActions) {
    queryClient.invalidateQueries({ queryKey: ['getBatchesSummary'] });
    if (context.toaster) {
      displayGenericSuccessToaster(
        t('recruiter_network.queue.toaster.success', {
          success: finalActionCount ?? batchedActions * totalCount
        })
      );
    }
  } else if (hasError) {
    if (callback) callback();
    if (context.toaster) {
      displayGenericErrorToaster(t('general.error.general'));
    }
  } else {
    if (callback) callback();
    if (context.toaster) {
      displayGenericSuccessToaster(context.toaster);
    }
  }
}
